/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ReactNode } from "react"
import { memo, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import { IconEnvelopePlusOutline18 as MailPlus } from "nucleo-ui-outline-18"

import type { ProjectStatusTypes } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	IssuesAmountIndicator,
	ProjectStatusColors,
} from "@productlane/ui"

import type { ProjectOrIssue } from "@/lib/project-or-issue-helpers"
import { showsPortalVisibility } from "@/components/shared/visibility-labels"
import { VisibilityToggle } from "@/components/shared/visibility-toggle"
import { useDetailLinkProps } from "@/lib/detail-drawer"
import { getProjectIcon } from "@/lib/projects"
import { useZeroWorkspace } from "@/lib/zero"
import { IssuePrioritySelect } from "@/screens/app/issues/issue-priority-select"
import { IssueStatusSelect } from "@/screens/app/issues/issue-status-select"
import { ProjectIconSelect } from "@/screens/app/projects/project-icon-select"

type Props = ProjectOrIssue & {
	className?: string
	maxPainLevel: number
	hidePriority?: boolean
	hideItems?: boolean
	trailingAction?: ReactNode
	/** Sits in the row's left padding, so it never shifts the columns. */
	dragHandle?: ReactNode
}

export const ProjectOrIssueListItem = memo(
	({
		type,
		maxPainLevel,
		hidePriority,
		data,
		className,
		hideItems: _hideItems,
		trailingAction,
		dragHandle,
	}: Props) => {
		const name = type === "project" ? data.name : data.title
		const linkProps = useDetailLinkProps(type, data.id)
		const showVisibility = showsPortalVisibility(useZeroWorkspace())
		const painLevelPercent = useMemo(() => {
			let relativeScore = 0
			if (maxPainLevel) {
				relativeScore = (data?.totalScore ?? 0) / maxPainLevel
			}
			return relativeScore * 100
		}, [data.totalScore, maxPainLevel])

		const Icon = useMemo(() => {
			if (type === "project") {
				return getProjectIcon(data.icon)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [type])

		const statusColor =
			type === "project"
				? (data.stateColor ??
					ProjectStatusColors[(data.state ?? "backlog") as ProjectStatusTypes])
				: undefined

		const projectIssuesAmount: number | null =
			type === "project" ? (data.issues?.length ?? 0) : null

		// Dynamic grid template based on type
		const gridTemplate =
			type === "project"
				? "grid-cols-[auto_1fr_auto_auto_auto]" // icon | title | indicators | pain-level | visibility
				: "grid-cols-[auto_auto_auto_1fr_auto_auto_auto]" // icon/priority | status | identifier | title | indicators | pain-level | visibility

		return (
			<div>
				<div className={cn(className)}>
					<Link
						className={cn(
							"group relative grid min-h-[2.8rem] items-center gap-3 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-850",
							gridTemplate,
							className,
							{
								"pl-2 pr-3": !hidePriority,
								"px-5": hidePriority,
							},
						)}
						to={linkProps.to}
						onClick={linkProps.onClick}
					>
						{dragHandle}

						{/* Icon/Priority column */}
						<div className="flex items-center">
							{type === "project" && Icon && (
								<div
									onClick={(e) => {
										e.preventDefault()
									}}
								>
									<ProjectIconSelect
										project={data}
										iconVariant="dark-ghost"
										overrideColor={statusColor}
									/>
								</div>
							)}
						</div>

						{/* Issue-specific columns - only rendered for issues */}
						{type === "issue" && (
							<div className="flex items-center gap-3">
								{/* Status column */}
								<div className="flex items-center justify-center">
									<div onClick={(e) => e.preventDefault()}>
										<IssueStatusSelect issue={data} />
									</div>
								</div>

								{/* Identifier column - hidden on small screens */}
								<div className="hidden flex-none tabular-nums text-gray-500 md:block">
									{data.identifier}
								</div>

								{!hidePriority && (
									<div
										className="hidden md:block"
										onClick={(e) => e.preventDefault()}
									>
										<IssuePrioritySelect issue={data} />
									</div>
								)}
							</div>
						)}

						{/* Title and description section - flexible width */}
						<div className="flex min-w-0 items-center gap-3 font-medium">
							<span className="min-w-0 truncate">{name}</span>
						</div>

						{/* Indicators column - important */}
						<div className="flex items-center justify-end gap-3">
							{type === "project" && data.archived && (
								<Badge
									variant="default"
									className="whitespace-nowrap rounded-full px-2"
								>
									<MailPlus className="h-3 w-3 text-tertiary" />
									<span className="whitespace-nowrap">Early access</span>
								</Badge>
							)}
							{projectIssuesAmount !== null && projectIssuesAmount > 0 && (
								<IssuesAmountIndicator
									amount={projectIssuesAmount}
									className="w-fit"
								/>
							)}
						</div>

						{/* Pain level column */}
						<div className="flex w-20 flex-none items-center gap-2 text-gray-700 dark:text-gray-300">
							<div className="relative h-1 w-10 rounded bg-gray-200 dark:bg-gray-800">
								<div
									className="absolute h-1 rounded bg-green-500"
									style={{ width: `${painLevelPercent}%` }}
								/>
							</div>
							<p className="w-7">{data.totalScore}</p>
						</div>

						{/* Trailing controls - right side */}
						{(trailingAction || showVisibility) && (
							<div
								className={cn(
									"flex flex-none justify-end",
									trailingAction ? "" : "min-w-7",
								)}
								onClick={(event) => {
									event.preventDefault()
									event.stopPropagation()
								}}
							>
								{trailingAction ?? (
									<VisibilityToggle
										type={type}
										id={data.id}
										isVisible={data.isVisible}
										portalInstanceIds={data.portalInstanceIds ?? []}
									/>
								)}
							</div>
						)}
					</Link>
				</div>
			</div>
		)
	},
)
